import usersApi from "@/api/users";
import userProfilesApi from "@/api/userProfiles";
import relationshipsApi from "@/api/relationships";
import specialtiesApi from "@/api/specialty";
import readingsApi from "@/api/readings";
import logger from "@/utils/logger";
import * as mutations from "@/store/types/mutation-types";
import * as actionTypes from "@/store/types/action-types";
import get from "lodash/get";
import { DateTime } from "luxon";
import wordGameApi from "@/api/word_game";
const actions = {};

const todaysDate = DateTime.now().toLocal().toFormat("yyyy-MM-dd");
const handleError = async (fn, ...args) => {
  try {
    return await fn(...args);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.getUserByAuth0Id] = async ({ commit }, { auth0Id }) => {
  try {
    const response = await usersApi.getUserByAuth0Id(auth0Id);
    logger.info(response.data);
    commit(mutations.SET_USER, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.getCurrentUserProfile] = async ({ commit, state }) => {
  try {
    const response = await userProfilesApi.getById(
      state.currentUser.user_profiles[0].id
    );
    logger.info(response.data);
    commit(mutations.SET_USER_PROFILE, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.getCurrentUsersDevices] = async ({ commit, state }) => {
  try {
    const response = await userProfilesApi.getUsersDevices(
      state.currentUser.user_profiles[0].id
    );
    logger.info(response.data);
    commit(mutations.SET_USERS_DEVICES, response.data);
    return response.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

// Get all relationships
actions[actionTypes.getRelationships] = async ({ commit }) => {
  try {
    const response = await relationshipsApi.getRelationships();
    logger.info(response.data);
    commit(mutations.SET_RELATIONSHIPS, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

// Get all specialties
actions[actionTypes.getSpecialties] = async ({ commit }) => {
  try {
    const response = await specialtiesApi.getSpecialties();
    logger.info(response.data);
    commit(mutations.SET_SPECIALTIES, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

// Get share history by user profile id
actions[actionTypes.getShareHistory] = async ({ commit, state }) => {
  try {
    const response = await readingsApi.getShareHistory(
      state.currentUser.user_profiles[0].id
    );
    logger.info(response.data);
    commit(mutations.SET_SHARE_HISTORY, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

// Post share history by user profile id
actions[actionTypes.postShareHistory] = async ({ commit, state }, { body }) => {
  try {
    const response = await readingsApi.postShareHistory(
      state.currentUser.user_profiles[0].id,
      body
    );
    logger.info(response.data);
    commit(mutations.POST_SHARE_HISTORY, response.data);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};
// Get the latest reading for device(s)
actions[actionTypes.getLatestReadingsForDevice] = async ({ state, commit }) => {
  try {
    // Call the currentUsersDevices action to fetch the devices
    const devices = state.currentUsersDevices;
    console.log("devices", devices);
    // Batch requests together and execute them in parallel
    const requests = devices.map((device) =>
      readingsApi.getReadingsForDevice(
        device.device_type.category.toLowerCase(),
        device.user_profile_id,
        "latest",
        device.id
      )
    );
    const responses = await Promise.allSettled(requests);

    // Use a map to associate device categories with mutation types and their state properties
    const deviceCategoryConfig = {
      BP_CUFF: {
        mutationType: mutations.SET_READING_FOR_BP_CUFF,
      },
      PULSE_OXIMETER: {
        mutationType: mutations.SET_READING_FOR_PULSE_OXIMETER,
      },
      THERMOMETER: {
        mutationType: mutations.SET_READING_FOR_THERMOMETER,
      },
      SCALE: {
        mutationType: mutations.SET_READING_FOR_SCALE,
      },
      GLUCOMETER: {
        mutationType: mutations.SET_READING_FOR_GLUCOMETER,
      },
    };

    // Initialize variables for tracking the latest readings and today's readings status
    const latestReadings = {};
    let isThereReadingForToday = false;

    responses.forEach((response, index) => {
      if (response.status === "fulfilled") {
        const device = devices[index];
        const config = deviceCategoryConfig[device.device_type.category];
        const readingObject = get(response.value, "readings[0]", null);

        // Update the mutation and check if there is a reading for today
        if (config && config.mutationType) {
          commit(config.mutationType, readingObject);
          latestReadings[device.device_type.category] = readingObject;

          if (
            !isThereReadingForToday &&
            (readingObject && readingObject.ts.slice(0, 10)) === todaysDate
          ) {
            isThereReadingForToday = true;
          }
        }
      }
    });

    // Update the global state with the latest readings and if there is a reading for today.
    commit(mutations.SET_LATEST_READING_FOR_DEVICE, latestReadings);
    commit(mutations.SET_LATEST_READINGS_FOR_TODAY, isThereReadingForToday);
    return latestReadings;
  } catch (e) {
    logger.error(e);
    throw e;
  }
};

// WORD GAME
actions[actionTypes.getWordGameWordOfTheDay] = async ({ commit }) => {
  const wordGameWord = await handleError(wordGameApi.getWordGameWordOfTheDay);
  if (wordGameWord) {
    commit(
      mutations.SET_WORD_GAME_WORD,
      wordGameWord.word_of_the_day.toUpperCase()
    );
    commit(mutations.SET_WORD_GAME_WORD_ID, wordGameWord.id);
  }
  return wordGameWord;
};

actions[actionTypes.getAllWordGameWords] = async ({ commit }) => {
  const allWordGameWords = await handleError(wordGameApi.getAllWordGameWords);
  commit(mutations.SET_ALL_WORD_GAME_WORDS, allWordGameWords.data);
  return allWordGameWords.data;
};

actions[actionTypes.attemptedWord] = async ({ commit }, word) => {
  const setGuessedWord = async () => {
    commit(mutations.SET_GUESSED_WORD, word);
  };
  await handleError(setGuessedWord);
};

// Word Games
actions[actionTypes.getWordGameRecord] = async ({ commit, state }) => {
  const fetchGameRecords = async () => {
    if (!state.currentUserProfile) {
      return null;
    }

    const [currentGameRecord, previousGameRecords] = await Promise.all([
      state.wordGameWordId
        ? wordGameApi.getWordGameRecord(
            state.currentUserProfile.id,
            state.wordGameWordId
          )
        : Promise.resolve(null),
      wordGameApi.getWordGameRecord(state.currentUserProfile.id),
    ]);

    if (currentGameRecord?.data?.[0]) {
      commit(mutations.SET_GAME_RECORD, currentGameRecord.data[0]);
    }

    if (previousGameRecords?.data?.[0]) {
      commit(mutations.SET_PREVIOUS_GAME_RECORD, previousGameRecords.data[0]);
    }

    return {
      currentGameRecord: currentGameRecord?.data,
      previousGameRecords: previousGameRecords?.data,
    };
  };

  return handleError(fetchGameRecords);
};

actions[actionTypes.setWordGameId] = async ({ commit, state }) => {
  const setGameId = async () => {
    const wordGame = state.gameRecord;
    if (wordGame) {
      commit(mutations.SET_WORD_GAME_ID, wordGame.id);
    }
  };
  return handleError(setGameId);
};

actions[actionTypes.getAllGuessedWords] = async ({ commit, state }) => {
  /* Fetch all guessed words for a given word game and updates the state with the guessed words and the number of attempted guesses.*/
  try {
    if (!state.wordGameId) {
      return [];
    }
    const allGuessedWords = await wordGameApi.getAllGuessedWords(
      state.currentUserProfile.id,
      state.wordGameId
    );

    if (!allGuessedWords || !allGuessedWords.data) {
      return [];
    }

    const attempts = allGuessedWords.data;
    let currentIndex = 0;
    attempts.forEach(({ row_attempt, attempted_word }) => {
      const index = row_attempt - 1;
      state.guesses[index] = attempted_word;
      currentIndex = state.guesses.filter((word) => word !== "").length;
    });

    if (!allGuessedWords || !allGuessedWords.data) {
      return;
    }

    const letterBoxColors = {};

    for (let i = 0; i < allGuessedWords.data.length; i++) {
      const row = allGuessedWords.data[i];
      const boxColors = [];
      const wordOfDay = state.solution;
      const guessedWord = row.attempted_word;

      const letterCount = {};

      for (let j = 0; j < guessedWord.length; j++) {
        const letter = guessedWord[j];

        if (wordOfDay[j] === letter) {
          boxColors.push("green");
          if (!letterCount[letter]) {
            letterCount[letter] = 0;
          }
          letterCount[letter]++;
        } else if (
          wordOfDay.includes(letter) &&
          (!letterCount[letter] ||
            letterCount[letter] < wordOfDay.split(letter).length - 1)
        ) {
          boxColors.push("yellow");
          if (!letterCount[letter]) {
            letterCount[letter] = 0;
          }
          letterCount[letter]++;
        } else {
          boxColors.push("gray");
        }
      }

      letterBoxColors[row.row_attempt] = boxColors;
    }

    commit(mutations.SET_LETTER_BOX_COLORS, letterBoxColors);

    const missed = new Set(
      allGuessedWords.data.flatMap((word) => word.miss.split(","))
    );
    const found = new Set(
      allGuessedWords.data.flatMap((word) => word.found.split(","))
    );
    const hint = new Set(
      allGuessedWords.data.flatMap((word) => word.hint.split(","))
    );
    commit(mutations.SET_MISS, missed);
    commit(mutations.SET_FOUND, found);
    commit(mutations.SET_HINT, hint);
    commit(mutations.UPDATE_ATTEMPTED_GUESSES, currentIndex);
    commit(mutations.GET_ALL_GUESSED_WORDS, allGuessedWords.data);
    return allGuessedWords.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.createWordGame] = async (
  /* Creates a new word game if there is no game record for today, or updates the existing game record if there is one. */
  { state },
  { payload }
) => {
  try {
    let gameCreated;
    const recordExistForToday = state.gameRecord;
    const createdAtDate = recordExistForToday
      ? DateTime.fromISO(recordExistForToday.created_at).toLocal().toISODate()
      : null;
    if (createdAtDate === todaysDate) {
      return;
    } else {
      gameCreated = await wordGameApi.createWordGame(payload);
    }
    return gameCreated;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};
actions[actionTypes.updateWordGame] = async (
  /* Creates a new word game if there is no game record for today, or updates the existing game record if there is one. */
  { state },
  { payload }
) => {
  try {
    let updateWordGame;
    const recordExistForToday = state.gameRecord;
    if (recordExistForToday) {
      updateWordGame = await wordGameApi.updateWordGame(payload);
    }
    return updateWordGame;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.updateWordGameStreaks] = async ({ commit, state }) => {
  /* Updates the streaks in the state. */
  try {
    const updateStreaks = state.previousGameRecord;
    if (
      updateStreaks &&
      (updateStreaks.current_streak || updateStreaks.best_streak)
    ) {
      commit(mutations.SET_CURRENT_STREAK, updateStreaks.current_streak);
      commit(mutations.SET_BEST_STREAK, updateStreaks.best_streak);
    } else {
      commit(mutations.SET_CURRENT_STREAK, 0);
      commit(mutations.SET_BEST_STREAK, 0);
    }
    return updateStreaks;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.updateAttemptedGuesses] = async (
  { commit },
  guessCount
) => {
  /* Updates the number of attempted guesses in the state. */
  try {
    commit(mutations.UPDATE_ATTEMPTED_GUESSES, guessCount);
    return guessCount;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.updateGameWon] = async ({ commit, state }) => {
  /* Updates the game won status in the state. */
  try {
    const gameRecord = state.gameRecord;
    if (gameRecord && gameRecord.updated_at) {
      const gameDate = DateTime.fromISO(gameRecord.updated_at)
        .toLocal()
        .toFormat("yyyy-MM-dd");
      if (gameDate === todaysDate) {
        commit(mutations.UPDATE_GAME_WON, gameRecord.game_won);
        commit(mutations.UPDATE_PLAYING_GAME, gameRecord.playing_game);
      }
    }
    return gameRecord;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

actions[actionTypes.saveWordForRow] = async ({ state, commit }, payload) => {
  /* Saves the word for a given row in the database. */
  try {
    const savedWord = await wordGameApi.saveWordForRow(
      state.currentUserProfile.id,
      payload
    );
    commit(mutations.SAVE_WORD_ROW, payload);
    return savedWord.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};
actions[actionTypes.updateLettersGuessed] = (
  { commit },
  { guessedLetter, type }
) => {
  /* Updates the letters guessed in the state. */
  try {
    commit(mutations.UPDATE_LETTERS_GUESSED, { guessedLetter, type });
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

export default actions;
